import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { Banknote, Landmark, Pencil, Plus } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  AccountsMasterList,
  type AccountSelection,
} from '@/features/bank-accounts/accounts-master-list'
import { AddAccountFlow } from '@/features/bank-accounts/add-account-flow'
import {
  balancesQueryOptions,
  bankAccountKeys,
  bankAccountsQueryOptions,
  deleteBankAccount,
} from '@/features/bank-accounts/api'
import { BankAccountFormDrawer } from '@/features/bank-accounts/bank-account-form-drawer'
import { IntegrationStatement } from '@/features/bank-transactions/integration-statement'
import { AccountStatement } from '@/features/entries/account-statement'
import { integrationsQueryOptions } from '@/features/integrations/api/queries'
import { IntegrationStatusBadge } from '@/features/integrations/components/integration-status-badge'
import type { BankAccountOut, IntegrationOut } from '@/shared/api/types'
import { Badge } from '@/shared/ui/badge'
import { Button } from '@/shared/ui/button'
import { Card } from '@/shared/ui/card'
import { EmptyState } from '@/shared/ui/empty-state'
import { PrintButton } from '@/shared/ui/print-button'
import { Skeleton } from '@/shared/ui/skeleton'
import { PageHeader } from '@/widgets/app-shell/page-header'
import { RowDeleteButton } from '@/widgets/data-table/row-delete-button'
import { PrintHeader } from '@/widgets/print/print-header'

export const Route = createFileRoute('/_authed/gestao-de-caixa/contas-e-extratos')({
  // Só contas e saldos: o catálogo e as integrações vêm do processor e são
  // consultados NA TELA, para que ele fora do ar não derrube a rota.
  loader: ({ context }) =>
    Promise.all([
      context.queryClient.ensureQueryData(bankAccountsQueryOptions()),
      context.queryClient.ensureQueryData(balancesQueryOptions()),
    ]),
  component: ContasPage,
})

function ContasPage() {
  const { t } = useTranslation(['bankAccounts', 'nav'])
  // Cadastro (com bifurcação manual/integrado) e edição são fluxos distintos:
  // editar uma conta existente é sempre o form manual.
  const [cadastrando, setCadastrando] = useState(false)
  const [editando, setEditando] = useState<BankAccountOut | null>(null)
  // Seleção unificada do master-detail: conta MANUAL ou INTEGRAÇÃO.
  const [selectedKey, setSelectedKey] = useState<string | null>(null)

  const contas = useQuery(bankAccountsQueryOptions())
  // Integrações (Hatch etc.) vêm do processor. Contam para o empty state (senão
  // ele escondia a integrada quando não havia conta manual) E entram na lista
  // como itens SELECIONÁVEIS — o extrato delas são as transações sincronizadas.
  const integracoes = useQuery(integrationsQueryOptions())
  const itens = contas.data?.items ?? []
  const integradas = integracoes.data?.items ?? []
  const temIntegracoes = integradas.length > 0

  // Casa a chave com manual ou integração; fallback = 1ª manual, senão 1ª integração.
  const selManual = itens.find((c) => c.id === selectedKey)
  const selIntegracao = integradas.find((i) => i.id === selectedKey)
  const selecao: AccountSelection | null = selManual
    ? { kind: 'manual', account: selManual }
    : selIntegracao
      ? { kind: 'integration', integration: selIntegracao }
      : itens[0]
        ? { kind: 'manual', account: itens[0] }
        : integradas[0]
          ? { kind: 'integration', integration: integradas[0] }
          : null
  const selectedKeyEfetivo =
    selecao === null
      ? null
      : selecao.kind === 'manual'
        ? selecao.account.id
        : selecao.integration.id

  const abrirCadastro = () => {
    setCadastrando(true)
  }

  const botaoNovo = (
    <Button variant="primary" onClick={abrirCadastro}>
      <Plus aria-hidden />
      {t('bankAccounts:new')}
    </Button>
  )

  return (
    <>
      <PageHeader
        title={t('nav:cash.accounts')}
        actions={
          <>
            <PrintButton />
            {botaoNovo}
          </>
        }
      />

      <div className="p-6">
        <PrintHeader title={t('nav:cash.accounts')} />
        {contas.isLoading ? (
          <Skeleton className="h-64 w-full" />
        ) : itens.length === 0 && !temIntegracoes ? (
          <EmptyState
            icon={Banknote}
            title={t('bankAccounts:empty.title')}
            description={t('bankAccounts:empty.description')}
            action={
              <Button variant="primary" size="lg" onClick={abrirCadastro}>
                {t('bankAccounts:new')}
              </Button>
            }
          />
        ) : (
          <div className="grid gap-4 lg:grid-cols-[minmax(280px,360px)_1fr]">
            <AccountsMasterList
              selectedKey={selectedKeyEfetivo}
              onSelect={(s) => {
                setSelectedKey(s.kind === 'manual' ? s.account.id : s.integration.id)
              }}
            />

            {selecao === null ? (
              <div className="border-line text-ink-subtle hidden items-center justify-center rounded-lg border border-dashed p-10 text-sm lg:flex">
                {t('bankAccounts:selectPrompt')}
              </div>
            ) : selecao.kind === 'manual' ? (
              <ManualAccountPanel account={selecao.account} onEdit={setEditando} />
            ) : (
              <IntegrationPanel integration={selecao.integration} />
            )}
          </div>
        )}
      </div>

      <AddAccountFlow open={cadastrando} onOpenChange={setCadastrando} />

      <BankAccountFormDrawer
        // key: remonta o drawer ao trocar de conta, para o defaultValues correto
        // ser lido na montagem (mesmo cuidado do bug fornecedor-vira-cliente).
        key={editando?.id ?? 'nenhuma'}
        open={editando !== null}
        onOpenChange={(aberto) => {
          if (!aberto) setEditando(null)
        }}
        {...(editando !== null && { account: editando })}
      />
    </>
  )
}

/** Painel direito de uma conta MANUAL: cabeçalho + extrato de lançamentos. */
function ManualAccountPanel({
  account,
  onEdit,
}: {
  readonly account: BankAccountOut
  readonly onEdit: (a: BankAccountOut) => void
}) {
  const { t } = useTranslation(['bankAccounts'])
  const ref =
    account.agency !== null || account.account_number !== null
      ? t('bankAccounts:accountRef', {
          agency: account.agency ?? '—',
          account: account.account_number ?? '—',
        })
      : null
  return (
    <div className="flex min-w-0 flex-col gap-4">
      <Card>
        <div className="flex items-center justify-between gap-3 px-5 py-4">
          <div className="flex min-w-0 items-center gap-3">
            <div className="bg-surface-3 flex size-10 shrink-0 items-center justify-center rounded-lg">
              <Landmark className="text-ink-muted size-5" aria-hidden />
            </div>
            <div className="min-w-0">
              <h2 className="text-ink truncate text-lg font-semibold">{account.name}</h2>
              {ref !== null && <p className="text-ink-subtle text-sm">{ref}</p>}
            </div>
          </div>
          <div className="group flex shrink-0 items-center gap-1">
            <button
              type="button"
              onClick={() => {
                onEdit(account)
              }}
              aria-label={t('bankAccounts:edit')}
              className="text-action hover:text-action rounded p-1"
            >
              <Pencil className="size-4" aria-hidden />
            </button>
            <RowDeleteButton
              ariaLabel={t('bankAccounts:edit')}
              confirmTitle={t('bankAccounts:deleteConfirm.title', { name: account.name })}
              confirmDescription={t('bankAccounts:deleteConfirm.description')}
              successMessage={t('bankAccounts:deleted')}
              onDelete={() => deleteBankAccount(account.id)}
              invalidateKey={bankAccountKeys.all()}
              conflictMessages={{ bank_account_in_use: t('bankAccounts:errorInUse') }}
            />
          </div>
        </div>
      </Card>
      {/* Extrato com saldo corrente + gráfico + ações (remonta ao trocar de conta). */}
      <AccountStatement key={account.id} account={account} />
    </div>
  )
}

/** Painel direito de uma INTEGRAÇÃO: cabeçalho + extrato das transações sincronizadas. */
function IntegrationPanel({ integration }: { readonly integration: IntegrationOut }) {
  const { t } = useTranslation(['bankAccounts'])
  return (
    <div className="flex min-w-0 flex-col gap-4">
      <Card>
        <div className="flex items-center justify-between gap-3 px-5 py-4">
          <div className="flex min-w-0 items-center gap-3">
            <div className="bg-surface-3 flex size-10 shrink-0 items-center justify-center rounded-lg">
              <Landmark className="text-ink-muted size-5" aria-hidden />
            </div>
            <div className="min-w-0">
              <h2 className="text-ink truncate text-lg font-semibold">{integration.name}</h2>
              <div className="mt-0.5 flex items-center gap-2">
                <Badge tone="info">{t('bankAccounts:nature.integrated')}</Badge>
                <IntegrationStatusBadge status={integration.status} />
              </div>
            </div>
          </div>
          <Link
            to="/configuracoes/integracoes"
            className="text-action hover:text-action-hover shrink-0 text-sm"
          >
            {t('bankAccounts:nature.manageAt')}
          </Link>
        </div>
      </Card>
      {/* Extrato = transações bancárias sincronizadas (remonta ao trocar). */}
      <IntegrationStatement key={integration.id} integrationId={integration.id} />
    </div>
  )
}
